<script setup lang="ts">
import { computed, StyleValue } from 'vue';
import { i18n } from '@/i18n';
import { LoadingStatus, loadingStatusHelpers } from './types';
import EzTableLoadingState from './EzTableLoadingState.vue';
import EzTableErrorState from './EzTableErrorState.vue';
import EzTableZeroMatchState from './EzTableZeroMatchState.vue';

const t = i18n.t;

/**
 * EzTableコンポーネントのプロパティ
 * @property {string[]} gridTemplateColumns - グリッドの各列の幅を定義する配列
 * @property {boolean} stickyHeader - ヘッダーを固定するかどうか
 * @property {boolean} stickyLeftColumns - 左側の列を固定するかどうか
 * @property {number[]} stickyColumnIndexes - 左側に固定する列のインデックス配列
 * @property {string} maxHeight - テーブルの最大高さ (例: '500px', '80vh')
 * @property {LoadingStatus} loadingStatus - テーブルのローディング状態
 */
type Props = {
  gridTemplateColumns: string[];
  stickyHeader?: boolean;
  stickyLeftColumns?: boolean;
  stickyColumnIndexes?: number[];
  maxHeight?: string;
  loadingStatus?: LoadingStatus;
  isVerticalScrollable?: boolean;
};

/**
 * イベント定義
 */
interface Emits {
  /** スクロールイベント */
  scroll: [e: Event];
  /** エラー状態での再試行ボタンクリック時 */
  retry: [];
}

const props = withDefaults(defineProps<Props>(), {
  stickyHeader: false,
  stickyLeftColumns: false,
  /**
   *
   */
  stickyColumnIndexes: () => [],
  maxHeight: 'auto',
  /**
   *
   */
  loadingStatus: () => ({ type: 'success' }),
  isVerticalScrollable: true,
});

const emits = defineEmits<Emits>();

const gridTemplateColumnsStyle = computed(() => props.gridTemplateColumns.join(' '));

/**
 * テーブルのスタイルを計算する
 */
const tableStyle = computed((): StyleValue => {
  return {
    maxHeight: props.maxHeight !== 'auto' ? props.maxHeight : undefined,
    overflowY: props.isVerticalScrollable ? 'auto' : 'hidden',
  };
});

/**
 * 現在のローディング状態の判定を行う
 */
const currentLoadingStatus = computed(() => props.loadingStatus);

/**
 * 通常のテーブルコンテンツを表示するかどうか
 */
const shouldShowTable = computed(() => {
  return loadingStatusHelpers.shouldShowContent(currentLoadingStatus.value);
});

/**
 * エラー状態での再試行ハンドラ
 */
const handleRetry = () => {
  emits('retry');
};
</script>

<template>
  <div
    class="ez-table"
    :style="tableStyle"
    @scroll="
      (e) => {
        emits('scroll', e);
      }
    "
  >
    <!-- 通常のテーブル表示 -->
    <template v-if="shouldShowTable">
      <!-- ヘッダー -->
      <div class="table__header-container" :class="{ 'table__header-row--sticky': props.stickyHeader }">
        <div
          :class="{
            'table__header-row': true,
          }"
        >
          <slot name="header" />
        </div>
      </div>
      <!-- ボディ -->
      <slot name="body" />
    </template>

    <!-- ローディング状態 -->
    <template v-else-if="loadingStatusHelpers.isLoading(currentLoadingStatus)">
      <slot name="loading">
        <EzTableLoadingState />
      </slot>
    </template>

    <!-- エラー状態 -->
    <template v-else-if="loadingStatusHelpers.isError(currentLoadingStatus)">
      <slot name="error" :error-message="currentLoadingStatus" :retry="handleRetry">
        <EzTableErrorState @retry="handleRetry" />
      </slot>
    </template>

    <!-- ゼロマッチ状態 -->
    <template v-else-if="loadingStatusHelpers.isZeroMatch(currentLoadingStatus)">
      <slot name="zero-match">
        <EzTableZeroMatchState />
      </slot>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.ez-table {
  width: 100%;
  height: 100%;
  overflow-x: auto;
  .table__header-container {
    width: max-content;
  }
  .table__header-row,
  .table__row {
    display: grid;
    grid-template-columns: v-bind(gridTemplateColumnsStyle);
    width: 100%;
  }

  .table__header-row--sticky {
    position: sticky;
    top: 0;
    z-index: 3;
    background-color: $surfaceSecondary;
  }
}
</style>
